/**
 * T-27 — timing marks feeding the commit event's `opened_ts`/`saved_ts`
 * (AC-1g-22 perf budgets: tap→recording-live p95 ≤1.0s, save→ack ≤300ms).
 * `performance.mark`/`.measure` drive an on-device debug overlay for pilot
 * bench runs; the wall-clock ISO pair is what actually rides the envelope.
 *
 * Both-or-neither (named test `test_timing_pair_both_or_neither`, the T-27
 * half — full server-side wiring is T-28, not this batch): if no `opened`
 * mark was recorded, `markSaved` returns `undefined` rather than a
 * saved-only pair, so a caller can never emit a partial pair.
 */

import type { CaptureEnvelope } from "../outbox/types";

export interface TimingPair {
  opened_ts: string;
  saved_ts: string;
}

interface OpenRecord {
  wallIso: string;
}

const opens = new Map<string, OpenRecord>();

function safeMark(name: string): void {
  try {
    performance.mark(name);
  } catch {
    // performance.mark is unavailable in some non-browser harnesses; the
    // wall-clock timestamp below is the value that actually matters for
    // the emitted envelope, so a missing Performance API is not fatal.
  }
}

/** Called at tap-to-record (the honest "opened" signal — see
 * `components/ActiveCapture.tsx`, bound to `MediaRecorder.onstart`). */
export function markOpened(capture_id: string): void {
  safeMark(`mark:opened:${capture_id}`);
  opens.set(capture_id, { wallIso: new Date().toISOString() });
}

/** Called at save-tap (ack render). Returns the pair to attach to the
 * envelope, or `undefined` if no matching `markOpened` was ever recorded
 * for this capture_id (both-or-neither: never returns a saved-only pair). */
export function markSaved(capture_id: string): TimingPair | undefined {
  const open = opens.get(capture_id);
  if (!open) return undefined;

  safeMark(`mark:saved:${capture_id}`);
  opens.delete(capture_id);
  try {
    performance.measure(
      `mark:tap-to-ack:${capture_id}`,
      `mark:opened:${capture_id}`,
      `mark:saved:${capture_id}`,
    );
  } catch {
    // See safeMark above.
  }

  return { opened_ts: open.wallIso, saved_ts: new Date().toISOString() };
}

/** Attaches a timing pair to an envelope, or leaves both fields unset —
 * never sets exactly one (mirrors the v2 schema's both-or-neither
 * dependency on `_envelope.opened_ts`/`saved_ts`). */
export function applyTimingPair(
  envelope: CaptureEnvelope,
  pair: TimingPair | undefined,
): CaptureEnvelope {
  if (!pair) return envelope;
  return { ...envelope, opened_ts: pair.opened_ts, saved_ts: pair.saved_ts };
}

/** The both-or-neither invariant itself, exposed standalone so the named
 * test can assert it directly against arbitrary envelope-shaped fixtures
 * (including the published schema's invalid/timing-opened-without-saved.json). */
export function hasValidTimingPair(envelope: { opened_ts?: string; saved_ts?: string }): boolean {
  const hasOpened = envelope.opened_ts !== undefined;
  const hasSaved = envelope.saved_ts !== undefined;
  return hasOpened === hasSaved;
}

/** Debug-overlay data source for pilot-device bench runs (T-27 Files).
 * No dedicated UI component is listed for T-27 in S2 — this exposes the
 * measured durations for whichever nav surface later renders them. */
export function getDebugTimings(): Array<{ capture_id: string; durationMs: number }> {
  if (typeof performance.getEntriesByType !== "function") return [];
  return performance
    .getEntriesByType("measure")
    .filter((e) => e.name.startsWith("mark:tap-to-ack:"))
    .map((e) => ({
      capture_id: e.name.slice("mark:tap-to-ack:".length),
      durationMs: e.duration,
    }));
}

export function _resetMeasureStateForTests(): void {
  opens.clear();
  try {
    performance.clearMarks();
    performance.clearMeasures();
  } catch {
    // no-op outside browser-like environments
  }
}
